/**
 * In-app banner rendered when the `appointment.imminent` SSE event
 * fires (~15 min before an appointment starts). Sits at the top-right
 * of the viewport so it doesn't interfere with the main flow.
 *
 * The data lives in `useAppointmentReminder`'s return value; this
 * component only renders. Wire by:
 *
 *   const { handler, lastToast, dismissToast } = useAppointmentReminder('agent');
 *   useRegisterEventHandler('appointment.imminent', handler);
 *   <AppointmentReminderBanner toast={lastToast} onDismiss={dismissToast} />
 */
import type { AppointmentReminderToast } from '../../hooks/useAppointmentReminder';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';

interface Props {
  toast: AppointmentReminderToast | null;
  onDismiss: () => void;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(readerLocale(), {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function minutesUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 60_000));
}

export default function AppointmentReminderBanner({
  toast,
  onDismiss,
}: Readonly<Props>): JSX.Element | null {
  if (!toast) return null;

  const mins = minutesUntil(toast.startsAt);
  // Same phrasing for agent + client viewers — the counterparty name
  // is set per perspective inside useAppointmentReminder so the noun
  // is correct either way.
  const headline = `Call with ${toast.counterpartyName} in ${mins} min`;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 60,
        maxWidth: 360,
        background: colors.surface,
        color: colors.ink,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: 14,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <strong style={{ fontSize: 13, lineHeight: 1.3 }}>{headline}</strong>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss reminder"
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.muted,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ fontSize: 12, color: colors.muted }}>
        Starts {formatTime(toast.startsAt)}
      </div>
      {toast.purpose ? (
        <div style={{ fontSize: 12, color: colors.inkSoft }}>
          {toast.purpose}
        </div>
      ) : null}
    </div>
  );
}
